import React, { useState } from 'react';

import CardDeckSelector from './CardDeckSelector';
import { CardDeckType } from '../utils/types';

interface RoundControlsProps {
    onStartNewRound: (description: string) => void;
    onRevealVotes: () => void;
    onUpdateCardDeck: (deckType: CardDeckType, customValues?: string[]) => void;
    isAdmin: boolean;
    revealed: boolean;
    currentDeckType: CardDeckType;
    currentCardValues: string[];
}

const RoundControls: React.FC<RoundControlsProps> = ({
    onStartNewRound,
    onRevealVotes,
    onUpdateCardDeck,
    isAdmin,
    revealed,
    currentDeckType,
    currentCardValues,
}) => {
    const [description, setDescription] = useState('');
    const [showDeckSettings, setShowDeckSettings] = useState(false);
    const [isConfirmingReveal, setIsConfirmingReveal] = useState(false);

    const handleStartNewRound = () => {
        onStartNewRound(description.trim());
        setDescription('');
    };

    const handleRevealVotes = () => {
        if (isConfirmingReveal) {
            onRevealVotes();
            setIsConfirmingReveal(false);
        } else {
            setIsConfirmingReveal(true);

            // Auto-reset after 3 seconds
            setTimeout(() => {
                setIsConfirmingReveal(false);
            }, 3000);
        }
    };

    if (!isAdmin) {
        return null;
    }

    return (
        <div className="my-6 overflow-hidden">
            <div className="bg-white dark:bg-dark-card rounded-lg shadow-md border border-gray-100 dark:border-dark-border">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 px-4 py-3 border-b border-gray-200 dark:border-dark-border">
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-2 text-primary"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                        </svg>
                        Admin Controls
                    </h3>
                </div>

                <div className="p-4">
                    <button
                        onClick={() => setShowDeckSettings(!showDeckSettings)}
                        className="flex items-center justify-between w-full p-3 mb-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md transition-colors duration-200 border border-gray-200 dark:border-dark-border"
                    >
                        <span className="flex items-center font-medium">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 mr-2 text-gray-500 dark:text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                                />
                            </svg>
                            Card Deck Settings
                        </span>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-5 w-5 text-gray-500 dark:text-gray-400 transform transition-transform duration-200 ${showDeckSettings ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </button>

                    <div
                        className={`overflow-hidden transition-all duration-300 ${showDeckSettings ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                        <CardDeckSelector
                            currentDeckType={currentDeckType}
                            currentValues={currentCardValues}
                            onUpdateDeck={onUpdateCardDeck}
                        />
                    </div>

                    <div className="mt-4">
                        <div className="relative mb-3">
                            <input
                                type="text"
                                placeholder="Describe the user story or task..."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full p-3 pr-10 border border-gray-300 dark:border-dark-border dark:bg-dark-card dark:text-dark-text rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                            />
                            {description && (
                                <button
                                    onClick={() => setDescription('')}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleStartNewRound}
                                className="flex-1 flex items-center justify-center p-3 bg-secondary hover:bg-secondary-dark text-white rounded-md transition-colors duration-200 font-medium"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5 mr-2"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                Start New Round
                            </button>

                            {!revealed && (
                                <button
                                    onClick={handleRevealVotes}
                                    className={`
                                        flex-1 flex items-center justify-center p-3 rounded-md transition-all duration-200 font-medium
                                        ${
                                            isConfirmingReveal
                                                ? 'bg-red-500 hover:bg-red-600 text-white'
                                                : 'bg-primary hover:bg-primary-dark text-white'
                                        }
                                    `}
                                >
                                    {isConfirmingReveal ? (
                                        <>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5 mr-2"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                                />
                                            </svg>
                                            Confirm Reveal
                                        </>
                                    ) : (
                                        <>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5 mr-2"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                />
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                />
                                            </svg>
                                            Reveal Votes
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RoundControls;
